/**
 * Capability-based browser support.
 *
 * We used to allowlist browser families by user agent (Chrome, Chromium,
 * Edge, Opera, Brave, Firefox), which turned away every other engine -
 * Safari most notably - even though the app only depends on a handful of
 * platform APIs that those browsers have shipped for years. Detecting the
 * capabilities directly lets any browser that can actually run the app in,
 * keeps us honest when a new dependency raises the bar, and gives the user
 * a specific reason when their browser really can't run it.
 *
 * Critical capabilities block the app: without them the app either can't
 * store case data or can't encrypt it, so a half-working UI would be worse
 * than a clear message. Everything else degrades at the call site instead.
 */

const isFunction = (value) => typeof value === 'function';

/**
 * Feature-detect a Web Storage area. Access alone can throw (Chrome with
 * third-party/all cookies blocked raises SecurityError on the getter), and
 * a readable storage can still reject writes (Safari's legacy private mode
 * quota of 0), so probe with a real write.
 */
const canUseStorage = (name) => {
  try {
    const storage = window[name];
    if (!storage) return false;
    const probe = '__jbx_support_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

const hasSubtleCrypto = () => {
  try {
    const subtle = globalThis.crypto && globalThis.crypto.subtle;
    if (!subtle) return false;
    // The DEK lifecycle uses all of these: PBKDF2 unwrap of the stored key,
    // AES-GCM on every record, SHA-256 for the per-user database name.
    return (
      isFunction(subtle.encrypt) &&
      isFunction(subtle.decrypt) &&
      isFunction(subtle.digest) &&
      isFunction(subtle.importKey) &&
      isFunction(subtle.exportKey) &&
      isFunction(subtle.deriveKey) &&
      isFunction(subtle.generateKey)
    );
  } catch {
    return false;
  }
};

/**
 * Capabilities the app cannot run without, in the order we want them
 * reported. `label` is user-facing; `detail` explains what breaks.
 */
export const CRITICAL_CAPABILITIES = [
  {
    id: 'indexedDB',
    label: 'IndexedDB',
    detail: 'Cases and jurors are stored in the browser.',
    detect: () => {
      try {
        return !!globalThis.indexedDB && isFunction(globalThis.indexedDB.open);
      } catch {
        return false;
      }
    },
  },
  {
    id: 'webCrypto',
    label: 'Web Crypto',
    detail: 'Case data is encrypted before it is stored.',
    detect: hasSubtleCrypto,
  },
  {
    id: 'randomValues',
    label: 'Secure random values',
    detail: 'Used to generate encryption keys and record ids.',
    detect: () => {
      try {
        return isFunction(globalThis.crypto?.getRandomValues);
      } catch {
        return false;
      }
    },
  },
  {
    id: 'localStorage',
    label: 'Local storage',
    detail: 'Keeps you signed in between visits.',
    detect: () => canUseStorage('localStorage'),
  },
  {
    id: 'sessionStorage',
    label: 'Session storage',
    detail: 'Holds your encryption key for the current session.',
    detect: () => canUseStorage('sessionStorage'),
  },
  {
    id: 'fetch',
    label: 'Fetch and AbortController',
    detail: 'Used for every request to the Jurybox servers.',
    detect: () =>
      isFunction(globalThis.fetch) && isFunction(globalThis.AbortController),
  },
];

/**
 * @returns {Array<{id: string, label: string, detail: string}>} the critical
 *   capabilities this browser is missing - empty when the app can run.
 */
export const getMissingCriticalCapabilities = () =>
  CRITICAL_CAPABILITIES.filter((capability) => {
    try {
      return !capability.detect();
    } catch {
      return true;
    }
  }).map(({ id, label, detail }) => ({ id, label, detail }));

/** True when every critical capability is present. */
export const isBrowserSupported = () =>
  getMissingCriticalCapabilities().length === 0;

/**
 * Web Crypto and service workers are only exposed in secure contexts, so an
 * app served over plain http fails the critical checks for a reason that has
 * nothing to do with the browser. Worth calling out separately so nobody
 * chases a browser bug that isn't there.
 */
export const isInsecureContext = () => {
  try {
    return window.isSecureContext === false;
  } catch {
    return false;
  }
};

/**
 * Nice-to-have capabilities. Each one has a fallback at the call site, so
 * these are for feature gating, not for blocking the app.
 */
export const supportsServiceWorker = () => {
  try {
    return 'serviceWorker' in navigator;
  } catch {
    return false;
  }
};

export const supportsWebAuthn = () => {
  try {
    return (
      isFunction(globalThis.PublicKeyCredential) &&
      !!navigator.credentials &&
      isFunction(navigator.credentials.create) &&
      isFunction(navigator.credentials.get)
    );
  } catch {
    return false;
  }
};

export const supportsWebSockets = () => {
  try {
    return isFunction(globalThis.WebSocket);
  } catch {
    return false;
  }
};

export const supportsRandomUUID = () => {
  try {
    return isFunction(globalThis.crypto?.randomUUID);
  } catch {
    return false;
  }
};

/**
 * Hosts whose subdomains are separate sites for cookie purposes, because the
 * host itself is a public suffix. `d2hie....cloudfront.net` and
 * `api.juryboxapp.com` are cross-site even though both are "ours".
 */
const PUBLIC_SUFFIXES = [
  'cloudfront.net',
  'amazonaws.com',
  'github.io',
  'pages.dev',
  'vercel.app',
  'netlify.app',
];

/**
 * Best-effort registrable domain ("site"). Not a full public-suffix list -
 * enough to tell apart the hosts Jurybox is actually served from.
 */
export const getSite = (host) => {
  const lower = String(host || '').toLowerCase();
  if (!lower) return '';
  // localhost, IP literals and anything with a colon (IPv6) is its own site.
  if (lower === 'localhost' || lower.includes(':') || /^\d{1,3}(\.\d{1,3}){3}$/.test(lower)) {
    return lower;
  }

  const suffix = PUBLIC_SUFFIXES.find(
    (candidate) => lower === candidate || lower.endsWith(`.${candidate}`),
  );
  const labelsToKeep = suffix ? suffix.split('.').length + 1 : 2;
  return lower.split('.').slice(-labelsToKeep).join('.');
};

/**
 * True when the API lives on a different site than the page, which makes the
 * session cookie a cross-site cookie. Safari blocks those by default (Prevent
 * Cross-Site Tracking) and other browsers are moving the same way, so this is
 * the difference between "signed in" and a redirect loop back to /signin.
 *
 * app.juryboxapp.com -> api.juryboxapp.com is same-site and fine; the
 * CloudFront distribution URL -> api.juryboxapp.com is not.
 */
export const isCrossSiteApi = (apiUrl) => {
  try {
    const apiHost = new URL(apiUrl, window.location.href).hostname;
    return getSite(apiHost) !== getSite(window.location.hostname);
  } catch {
    return false;
  }
};

/** Snapshot of the optional capabilities, handy for logging and Sentry. */
export const getOptionalCapabilities = () => ({
  serviceWorker: supportsServiceWorker(),
  webAuthn: supportsWebAuthn(),
  webSockets: supportsWebSockets(),
  randomUUID: supportsRandomUUID(),
});
